import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { FaIcon } from '@ifixit/ui/components/icon';
import type { HTMLAttributes, ReactNode } from 'react';
import { IconActionButton } from './IconActionButton';

/** The shared shell for an editor's removable rows: one padding, a min height
 *  so single-line rows land at the same height whatever they hold. */
export const LIST_ROW_CLASS =
   'flex min-h-12 items-center gap-2 rounded-lg border border-gray-300 p-2';

/**
 * A hover/focus tooltip on a control, replacing the title= attribute (which
 * never shows on touch or keyboard focus). The @ifixit/ui Tooltip wraps its
 * own provider, so no page-level setup is needed; triggers keep their own
 * aria-labels.
 */
export function ActionTooltip({ label, children }: { label: string; children: ReactNode }) {
   return (
      <Tooltip>
         <TooltipTrigger asChild>{children}</TooltipTrigger>
         <TooltipContent>{label}</TooltipContent>
      </Tooltip>
   );
}

/** A list row's delete control, sized to LIST_ROW_CLASS rows. */
export function RemoveButton({
   label,
   onClick,
   disabled = false,
}: {
   label: string;
   onClick: () => void;
   disabled?: boolean;
}) {
   return (
      <IconActionButton
         label={label}
         icon="trash-can"
         disabled={disabled}
         onClick={onClick}
         className="size-7 shrink-0 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-30"
      />
   );
}

/**
 * The quiet dashed box an empty row list renders: the add control below it is
 * the action, this just says the empty list isn't broken.
 */
export function EmptyListNotice({ icon, children }: { icon: string; children: ReactNode }) {
   return (
      <div className="mb-2 flex items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600">
         <FaIcon name={icon} className="size-4" />
         {children}
      </div>
   );
}

/**
 * A row's open-in-a-new-tab icon link, in the same size-7 slot as the other
 * row controls — with the focus ring a plain styled <a> keeps losing.
 */
export function RowIconLink({
   label,
   href,
   icon,
   iconClassName = 'size-4',
}: {
   label: string;
   href: string;
   icon: string;
   iconClassName?: string;
}) {
   return (
      <ActionTooltip label={label}>
         <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            className="flex size-7 shrink-0 items-center justify-center rounded text-gray-500 outline-none hover:bg-gray-100 hover:text-brand-600 focus-visible:ring-[3px] focus-visible:ring-ring/50"
         >
            <FaIcon name={icon} className={iconClassName} />
         </a>
      </ActionTooltip>
   );
}

// The grip is a keyboard-reorder button when a caller passes handleProps a
// label (tabIndex/role/aria-label come from the hook): focusable, with the
// arrow keys moving the row. aria-hidden lives on the icon, not the span, so
// it can't hide that button from screen readers.
export function DragHandle(props: HTMLAttributes<HTMLSpanElement>) {
   return (
      <span
         {...props}
         className="relative flex size-7 shrink-0 cursor-grab items-center justify-center rounded text-gray-300 outline-none hover:text-gray-500 focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
      >
         <FaIcon name="grip-vertical" className="size-3.5" aria-hidden />
      </span>
   );
}
